/**
 * Viewport layout and the single screen↔world coordinate pair (KTD2).
 *
 * `src/render/` owns this and nothing else derives screen↔world independently.
 * DPR, letterbox offset, the integer upscale factor, and the orthographic
 * projection all fold into one chain; two units inverting it separately
 * disagree, and the symptom is clicks landing on the wrong unit.
 *
 * Deliberately free of WebGL so the arithmetic is unit-testable in Node. The
 * only Three.js used here is vector maths, which needs no context.
 */

import { Vector3 } from 'three'
import {
  INTERNAL_WIDTH,
  INTERNAL_HEIGHT,
  MAP_SIZE,
  PIXEL_SCALE,
  PIXELS_PER_CELL,
} from '../core/config.js'

/** Locked camera orientation. Rotation is not a player control (R23). */
export const CAMERA_YAW = Math.PI / 4
export const CAMERA_PITCH = Math.atan(Math.SQRT1_2) // the classic 2:1 dimetric angle
/** Vertical extent of the reference view, in world cells. */
export const VIEW_HEIGHT_CELLS = INTERNAL_HEIGHT / PIXELS_PER_CELL

export interface ViewportRect {
  /** CSS pixels, relative to the window. */
  x: number
  y: number
  width: number
  height: number
}

export interface Layout {
  /** The area the world canvas occupies — the window minus the sidebar. */
  rect: ViewportRect
  /** Whole-number upscale from the internal target. Never fractional. */
  scale: number
  /** Internal render-target size, derived from the viewport and the scale. */
  internalWidth: number
  internalHeight: number
  /** Residual centering offset inside `rect`, at most `scale - 1` pixels. */
  offsetX: number
  offsetY: number
  /** Device pixel ratio the backing store is sized for. */
  dpr: number
}

/**
 * Lay out the world canvas so it **fills** the available area.
 *
 * The pixel *density* is fixed — one internal pixel is always `PIXEL_SCALE`
 * display pixels, which is what keeps the grid stable and the dither from
 * crawling (KTD2). What follows the window is the *extent*: a larger viewport
 * gets a larger internal target and therefore shows more of the map, rather
 * than the same 480×270 image sitting inside a black letterbox.
 *
 * The residual is at most `scale - 1` pixels on each axis, which is centred.
 */
export function computeLayout(
  windowWidth: number,
  windowHeight: number,
  sidebarWidth: number,
  dpr = 1,
  pixelScale = PIXEL_SCALE,
): Layout {
  const rect: ViewportRect = {
    x: 0,
    y: 0,
    width: Math.max(1, windowWidth - sidebarWidth),
    height: Math.max(1, windowHeight),
  }
  const scale = Math.max(1, Math.floor(pixelScale))
  const internalWidth = Math.max(64, Math.floor(rect.width / scale))
  const internalHeight = Math.max(64, Math.floor(rect.height / scale))

  return {
    rect,
    scale,
    internalWidth,
    internalHeight,
    offsetX: Math.floor((rect.width - internalWidth * scale) / 2),
    offsetY: Math.floor((rect.height - internalHeight * scale) / 2),
    dpr,
  }
}

/**
 * The locked camera basis, and the world-space step corresponding to one
 * internal-target pixel along each screen axis.
 *
 * Storing the camera position as **integer pixel offsets** and deriving the
 * world target from these steps is what makes whole-pixel snapping structural
 * rather than something a later `Math.round` has to remember to do. Sub-pixel
 * camera motion is the cause of dither crawl, and R23 makes panning the normal
 * case rather than an edge case.
 */
export class CameraBasis {
  /** Unit vector from camera toward its target. */
  readonly forward: Vector3
  /** Unit screen-right, horizontal by construction. */
  readonly right: Vector3
  /** Unit screen-up in world space. */
  readonly up: Vector3
  /** World delta for one internal pixel of screen-right movement. */
  readonly stepRight: Vector3
  /** World delta for one internal pixel of screen-up movement, on the ground. */
  readonly stepUp: Vector3
  /** Half-extents of the orthographic frustum, in world units. */
  readonly halfWidth: number
  readonly halfHeight: number

  constructor(
    internalWidth = INTERNAL_WIDTH,
    internalHeight = INTERNAL_HEIGHT,
    pixelsPerCell = PIXELS_PER_CELL,
  ) {
    const viewHeightCells = internalHeight / pixelsPerCell
    const cp = Math.cos(CAMERA_PITCH)
    const sp = Math.sin(CAMERA_PITCH)
    // Camera sits above and to one side; forward points down at the ground.
    this.forward = new Vector3(
      -Math.cos(CAMERA_YAW) * cp,
      -sp,
      -Math.sin(CAMERA_YAW) * cp,
    ).normalize()

    const worldUp = new Vector3(0, 1, 0)
    this.right = new Vector3().crossVectors(this.forward, worldUp).normalize()
    this.up = new Vector3().crossVectors(this.right, this.forward).normalize()

    this.halfHeight = viewHeightCells / 2
    this.halfWidth = internalWidth / pixelsPerCell / 2

    const worldPerPixelX = (this.halfWidth * 2) / internalWidth
    const worldPerPixelY = (this.halfHeight * 2) / internalHeight

    // Screen-right maps to a horizontal world vector directly, because `right`
    // is horizontal by construction.
    this.stepRight = this.right.clone().multiplyScalar(worldPerPixelX)

    // Screen-up maps to the ground direction away from the camera, scaled by
    // how foreshortened that direction is. The ground plane is tilted relative
    // to the view, so one screen pixel up is more than one world unit back.
    const groundAway = new Vector3(-this.forward.x, 0, -this.forward.z).normalize()
    const foreshorten = groundAway.dot(this.up)
    this.stepUp = groundAway.multiplyScalar(worldPerPixelY / foreshorten)
  }

  /** Where the camera itself sits, given its ground target. */
  positionFor(target: Vector3, distance = 200): Vector3 {
    return target.clone().addScaledVector(this.forward, -distance)
  }
}

export interface CameraState {
  /** Integer internal-target pixels. Whole-pixel by construction. */
  panX: number
  panY: number
}

/** World-space ground target for an integer pan, relative to the map centre. */
export function targetForPan(basis: CameraBasis, state: CameraState, origin?: Vector3): Vector3 {
  const base = origin ?? new Vector3(MAP_SIZE / 2, 0, MAP_SIZE / 2)
  return base
    .clone()
    .addScaledVector(basis.stepRight, Math.round(state.panX))
    .addScaledVector(basis.stepUp, Math.round(state.panY))
}

/**
 * Convert a window-space CSS pixel to a position on the ground plane.
 *
 * Returns `null` when the point is outside the letterboxed canvas — a click in
 * the letterbox is not a click on the world, and silently clamping it would
 * order units to the map edge.
 */
export function screenToWorld(
  clientX: number,
  clientY: number,
  layout: Layout,
  basis: CameraBasis,
  camera: CameraState,
  out = new Vector3(),
): Vector3 | null {
  const localX = clientX - layout.rect.x - layout.offsetX
  const localY = clientY - layout.rect.y - layout.offsetY
  const px = localX / layout.scale
  const py = localY / layout.scale
  if (px < 0 || py < 0 || px > layout.internalWidth || py > layout.internalHeight) return null

  // Offsets from the centre of the internal target, in pixels.
  const dx = px - layout.internalWidth / 2
  const dy = layout.internalHeight / 2 - py

  const target = targetForPan(basis, camera)
  return out
    .copy(target)
    .addScaledVector(basis.stepRight, dx)
    .addScaledVector(basis.stepUp, dy)
}

/**
 * Convert a ground position to a window-space CSS pixel. The exact inverse of
 * `screenToWorld` for points on the ground plane.
 */
export function worldToScreen(
  world: Vector3,
  layout: Layout,
  basis: CameraBasis,
  camera: CameraState,
): { x: number; y: number } {
  const target = targetForPan(basis, camera)
  const rel = world.clone().sub(target)

  const pixelsPerCell = layout.internalHeight / (2 * basis.halfHeight)
  const dx = rel.dot(basis.right) * pixelsPerCell
  const dy = rel.dot(basis.up) * pixelsPerCell

  const px = layout.internalWidth / 2 + dx
  const py = layout.internalHeight / 2 - dy
  return {
    x: px * layout.scale + layout.offsetX + layout.rect.x,
    y: py * layout.scale + layout.offsetY + layout.rect.y,
  }
}

/** Follow the camera ray down to the first terrain surface, including cliff faces. */
export function projectOnSurface(
  point: Vector3,
  basis: CameraBasis,
  heightAt: (x: number, z: number) => number,
  ceiling: number,
  floor: number,
): Vector3 {
  const x = point.x
  const z = point.z
  const sx = basis.forward.x / basis.forward.y
  const sz = basis.forward.z / basis.forward.y
  const steps = Math.ceil((ceiling - floor) / 0.05)
  let high = ceiling
  for (let i = 1; i <= steps; i++) {
    let low = ceiling - (ceiling - floor) * i / steps
    if (low <= heightAt(x + sx * low, z + sz * low)) {
      for (let j = 0; j < 12; j++) {
        const mid = (low + high) / 2
        if (mid <= heightAt(x + sx * mid, z + sz * mid)) low = mid
        else high = mid
      }
      return point.set(x + sx * high, high, z + sz * high)
    }
    high = low
  }
  return point
}

/**
 * Clamp a pan so the view stays over the map.
 *
 * Clamping is against the **unoccluded viewport rect**, not the full window: an
 * edge-scroll zone that extends under the sidebar fires when the pointer is
 * over the HUD, which reads as the camera moving on its own.
 */
export function clampPan(
  basis: CameraBasis,
  state: CameraState,
  mapSize = MAP_SIZE,
): CameraState {
  const target = targetForPan(basis, state)
  // Roughly half the visible extent, so the map fills the view rather than the
  // camera drifting out over the void beyond the border wall.
  const margin = 14
  const lo = margin
  const hi = mapSize - margin

  if (target.x >= lo && target.x <= hi && target.z >= lo && target.z <= hi) return state

  // Solve for the pan that lands the target on the clamped point, then round —
  // clamping in world space and re-deriving keeps whole-pixel motion intact.
  const clampedX = Math.min(hi, Math.max(lo, target.x))
  const clampedZ = Math.min(hi, Math.max(lo, target.z))
  const base = new Vector3(mapSize / 2, 0, mapSize / 2)
  const rel = new Vector3(clampedX - base.x, 0, clampedZ - base.z)

  const a = basis.stepRight
  const b = basis.stepUp
  const aa = a.x * a.x + a.z * a.z
  const ab = a.x * b.x + a.z * b.z
  const bb = b.x * b.x + b.z * b.z
  const ra = rel.x * a.x + rel.z * a.z
  const rb = rel.x * b.x + rel.z * b.z
  const det = aa * bb - ab * ab
  return {
    panX: Math.round((ra * bb - rb * ab) / det),
    panY: Math.round((rb * aa - ra * ab) / det),
  }
}
